import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { Form, Image } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import type { AuthController } from "../../../api/src/auth/auth.controller";
import type { UnpackResponse } from "../../../api/src/lib";
import Content from "../components/Content";
import { useErrorMessage } from "../components/FormError";
import FormFieldError from "../components/FormFieldError";
import SubmitButton from "../components/SubmitButton";
import { useAuthenticatedMutationAsync } from "../lib/api";
import { capitalize } from "../lib/display";
import { getSchemaFieldLabel } from "../lib/forms";

/** Posts the connect `code` (or PAT) to `/auth/authorize` for the service. */
type SubmitCredentials = (code: string) => Promise<unknown>;

type UserCredentials = {
  username: string;
  password: string;
};

const credentialsSchema: yup.ObjectSchema<UserCredentials> = yup.object({
  username: yup.string().required().label("Username"),
  password: yup.string().required().label("Password"),
});

type PatCredential = {
  pat: string;
};

const patSchema: yup.ObjectSchema<PatCredential> = yup.object({
  pat: yup.string().required().label("Personal Access Token"),
});

/** Close the popup and refresh the opening window after a successful connect. */
function finishConnect(service: string | undefined) {
  window.opener.location.reload();
  window.opener?.console.debug(`Authorized Service: ${service}`);
  window.close();
}

/** Username/password connect form (e.g. Redtail) — submits `username:password`. */
const PasswordForm = ({
  service,
  submitCredentials,
}: {
  service: string | undefined;
  submitCredentials: SubmitCredentials;
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<UserCredentials>({
    mode: "onBlur",
    resolver: yupResolver(credentialsSchema),
  });

  const { errorMessage, resetError, setError } = useErrorMessage();

  const onSubmit = useMutation({
    mutationFn: async ({ username, password }: UserCredentials) => {
      resetError();

      try {
        await submitCredentials(`${username}:${password}`);
        finishConnect(service);
      } catch (err) {
        console.error("Failed to log in", err);
        setError("Username/password combination is invalid");
      }
    },
  });

  return (
    <Form onSubmit={handleSubmit((data) => onSubmit.mutateAsync(data))}>
      {errorMessage}
      <Form.Group controlId="form-username" className="mb-2">
        <Form.Label>
          {getSchemaFieldLabel(credentialsSchema.fields.username)}
        </Form.Label>
        <Form.Control type="text" {...register("username")} />
        <FormFieldError field={errors.username} />
      </Form.Group>
      <Form.Group controlId="form-password" className="mb-3">
        <Form.Label>
          {getSchemaFieldLabel(credentialsSchema.fields.password)}
        </Form.Label>
        <Form.Control type="password" {...register("password")} />
        <FormFieldError field={errors.password} />
      </Form.Group>
      <div>
        <SubmitButton label="Log In" isSubmitting={isSubmitting} />
      </div>
    </Form>
  );
};

/** Personal Access Token connect form (Slant) — submits the pasted PAT verbatim. */
const PatForm = ({
  service,
  submitCredentials,
}: {
  service: string | undefined;
  submitCredentials: SubmitCredentials;
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<PatCredential>({
    mode: "onBlur",
    resolver: yupResolver(patSchema),
  });

  const { errorMessage, resetError, setError } = useErrorMessage();

  const onSubmit = useMutation({
    mutationFn: async ({ pat }: PatCredential) => {
      resetError();

      try {
        await submitCredentials(pat.trim());
        finishConnect(service);
      } catch (err) {
        console.error("Failed to connect", err);
        setError(
          "That Personal Access Token was rejected. Check it and try again.",
        );
      }
    },
  });

  return (
    <Form onSubmit={handleSubmit((data) => onSubmit.mutateAsync(data))}>
      {errorMessage}
      <Form.Group controlId="form-pat" className="mb-3">
        <Form.Label>{getSchemaFieldLabel(patSchema.fields.pat)}</Form.Label>
        <Form.Control
          type="password"
          placeholder="slnt_pat_…"
          {...register("pat")}
        />
        <Form.Text className="text-muted">
          Create a token in Slant under Settings → API, then paste it here.
        </Form.Text>
        <FormFieldError field={errors.pat} />
      </Form.Group>
      <div>
        <SubmitButton label="Connect" isSubmitting={isSubmitting} />
      </div>
    </Form>
  );
};

const IntegrationCredentials = () => {
  const { service } = useParams();
  const navigate = useNavigate();

  // Slant authenticates with a pasted Personal Access Token; other services
  // (Redtail) use a username/password pair.
  const isPat = service === "slant";
  const icon = service === "redtail" ? "/icons/redtail-logo.jpg" : "";

  const fetchState = useAuthenticatedMutationAsync<
    UnpackResponse<AuthController["getOAuthState"]>
  >(`/auth/${service}/state`, async () => ({ method: "GET" }));

  // `/auth/authorize` refuses a code without a `state` the API issued to this
  // user for this service (APP-2183), so fetch one first.
  const submitCredentials = useAuthenticatedMutationAsync<
    UnpackResponse<AuthController["authorizeService"]>
  >(`/auth/authorize`, async (code: string) => {
    const { data } = await fetchState();
    return {
      method: "POST",
      body: JSON.stringify({
        service,
        code,
        state: data.state,
      }),
    };
  });

  useEffect(() => {
    if (window.opener === null || window.opener === window) {
      navigate("/");
    }
  }, [navigate]);

  return (
    <div className="p-4">
      <Content>
        <div className="d-flex flex-column align-items-center">
          <div className="mb-3 bg-light p-1 rounded">
            <Image src="/logo.svg" height={70} />
          </div>
          <h1 className="mb-1d-flex align-items-center">
            {isPat ? "Connect to " : "Log In to "}
            {icon ? (
              <div className="d-inline-block bg-light p-1 me-1 rounded">
                <Image src={icon} height={40} />
              </div>
            ) : null}
            {capitalize(service ?? "")}
          </h1>
          {isPat ? (
            <PatForm service={service} submitCredentials={submitCredentials} />
          ) : (
            <PasswordForm
              service={service}
              submitCredentials={submitCredentials}
            />
          )}
        </div>
      </Content>
    </div>
  );
};

export default IntegrationCredentials;
